import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { ApplicationConfigurationService, EFundService } from 'app/api';
import { environment } from 'environments/environment';
import { getUserOnSessionStorage, isNullOrEmpty } from 'app/shared/functions/functions';
import { AuthService } from 'app/common/services/auth.service';

@Component({
  selector: '[core-menu]',
  templateUrl: './core-menu.component.html',
  styleUrls: ['./core-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CoreMenuComponent implements OnInit {
  currentUser: any;

  @Input()
  layout = 'vertical';

  @Input()
  menu: any;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   *
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {CoreMenuService} _coreMenuService
   */
  constructor(private _changeDetectorRef: ChangeDetectorRef, private _coreMenuService: CoreMenuService,
    private applicationConfigurationService: ApplicationConfigurationService,
    private eFundService: EFundService,
    private authService: AuthService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {
    // Set the menu either from the input or from the service
    
    this.menu = this.menu || this._coreMenuService.getCurrentMenu(false);

    // Subscribe to the current menu changes
    this._coreMenuService.onMenuChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(async () => {
      this.currentUser = this._coreMenuService.currentUser;
      const userRole = this.currentUser?.roles[0];
    let canManualEncode = false;
    const isAdmin = userRole != 'Basic';
    const hasMemberCode = !isNullOrEmpty(this.currentUser?.memberCode);
    let canAccessAdminSetup = false;
    if(userRole == 'SuperAdmin'){
      canAccessAdminSetup = true;
      canManualEncode = true;
    }else if(userRole == 'Admin'){
      canAccessAdminSetup = true;
      canManualEncode = await this.checkManualEncodingAuthorization(this.currentUser?.id);
    }

    const hasEFundAccess = this.checkEFundModuleAuthorization();
    const canCreateEFundUser = this.checkEFundUserCreationAuthorization();
    const canCreateEFundBenefit = this.checkEFundBenefitCreationAuthorization();
    
      // Load menu
      if(isAdmin) {
        this.menu = this._coreMenuService.getCurrentMenu(true, { isAdmin: isAdmin, 
          canManualEncode: canManualEncode, hasEFundAccess: hasEFundAccess, 
          canCreateEFundUser: canCreateEFundUser, canCreateBenefit: canCreateEFundBenefit,
        canAccessAdminSetup: canAccessAdminSetup});
      }else {
        this.menu = this._coreMenuService.getCurrentMenu(true, { isAdmin: isAdmin, hasMemberCode: hasMemberCode, 
          hasEFundAccess: false, canCreateEFundUser: false, canCreateBenefit: false, canAccessAdminSetup: false });
      }
      

      this._changeDetectorRef.markForCheck();
    });
  }

  async checkManualEncodingAuthorization(userId): Promise<boolean> {
      const token = sessionStorage.getItem("token");
      if(token && !this.authService.isAuthenticated){
        return;
      }
      return new Promise<boolean>((resolve, reject) => {
        this.applicationConfigurationService
          .apiVversionApplicationConfigurationsApplicationConfigListByCategoryCategoryGet(
            "ManualEncodingAccess",
            environment.apiVersion
          )
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(
            (response: any) => {
              const data = response['data']['applicationConfigKeyAndValuePair'];
              if(response.succeeded){
                if(data.length > 0){
                  const authorizedUsers = [...data].map((user) => user['value']);                  
                  resolve(authorizedUsers.includes(userId));
                }else{
                  resolve(false);
                }
              }
            },
            (error) => {
              reject(error);
            }
          );
      });
    }

  checkEFundModuleAuthorization(): boolean {
      const token = sessionStorage.getItem("token");
      if(token && !this.authService.isAuthenticated){
        return;
      }

      const eFundUserAccess = getUserOnSessionStorage("eFundUserAccess");
      if(!eFundUserAccess) {
        return false;
      }else{
        return eFundUserAccess.canViewEFundModule;
      }
    }

  checkEFundUserCreationAuthorization(): boolean {
    const token = sessionStorage.getItem("token");
    if(token && !this.authService.isAuthenticated){
      return;
    }

    const eFundUserAccess = getUserOnSessionStorage("eFundUserAccess");
    if(!eFundUserAccess) {
      return false;
    }else{
      return eFundUserAccess.canCreateUserAccess;
    }
  }

  checkEFundBenefitCreationAuthorization(): boolean {
    const token = sessionStorage.getItem("token");
    if(token && !this.authService.isAuthenticated){
      return;
    }

    const eFundUserAccess = getUserOnSessionStorage("eFundUserAccess");
    if(!eFundUserAccess) {
      return false;
    }else{
      return eFundUserAccess.canBulkUpload;
    }
  }
}
